<script setup lang="ts">
/**
 * The sidebar's header: whose portal this is. Company name and plan, from the
 * dashboard store, so it is right on every page without each view passing it.
 * There is nothing to switch to (one Lark tenant per session), so it is a link
 * home rather than a switcher that opens onto nothing.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { usePlan } from '@lark-automadoc/shared-ui'
import { useAuthStore } from '@/stores/auth'
import { useDashboardStore } from '@/stores/dashboard'

defineProps<{ collapsed: boolean }>()

const { t } = useI18n()
const auth = useAuthStore()
const dashboard = useDashboardStore()
const { tenant, planStatus, usageStatus, loaded } = storeToRefs(dashboard)
const { planDisplay, isPro } = usePlan({ planStatus, usageStatus })

const companyName = computed(() => tenant.value?.name || auth.user?.tenantName || t('myCompany'))
</script>

<template>
  <!-- custom: a plain RouterLink would add a second aria-current="page"
       beside the Dashboard entry. -->
  <RouterLink v-slot="{ href, navigate }" to="/" custom>
    <a
      :href="href"
      :aria-label="collapsed ? companyName : undefined"
      :class="[
        'flex min-w-0 items-center gap-2.5 rounded-lg p-1 outline-none hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-blue-500/70 dark:hover:bg-white/5 motion-safe:transition-colors',
        collapsed ? 'justify-center' : '',
      ]"
      @click="navigate"
    >
      <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-gray-900">
        <img src="/logo.svg" alt="" class="h-5 w-5" />
      </span>
      <span v-if="!collapsed" class="min-w-0 flex-1 leading-tight">
        <span class="block truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{{ companyName }}</span>
        <span class="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span class="truncate">AutomaDoc</span>
          <template v-if="loaded">
            <span aria-hidden="true">·</span>
            <span :class="isPro ? 'font-medium text-blue-700 dark:text-blue-300' : ''">{{ planDisplay }}</span>
          </template>
        </span>
      </span>
    </a>
  </RouterLink>
</template>
